import classNames from "classnames";
import { useEffect, useState } from "react";
import { useModalControl } from "../../../../customHooks/useModalControl";
import { formatToCurrency } from "../../../../helperFunctions";
import {
  useApproveSharesMutation,
  useFetchPendingSharesApprovalsQuery,
} from "../../../../redux/features/approvals/approvals.api";
import {
  SharesApprovalQuery,
  SharesApprovalsResponse,
  SharesBulkApprovalsResponse,
} from "../../../../redux/features/approvals/approvals.type";
import { SingleCooperative } from "../../../../redux/features/cooperative/cooperative.type";
import {
  SingleBulkShareLog,
  SingleSharesLog,
} from "../../../../redux/features/shares/shares.type";
import { ApprovalActionDetails } from "../../../../utils/types";
import { ApprovalButtonSet } from "../../../buttons/approvalButtonSet";
import { ConfirmationModal } from "../../../modals/app_modals/confirmationModal";
import { BulkContributionsEntriesModal } from "../../../modals/app_modals/contributions/bulkUploadEntries";
import { ImageViewModal } from "../../../modals/app_modals/imageViewModal";
import { TabNav } from "../../../navs/tabNav";
import { BulkSharesApproval } from "./bulkSharesApproval";
import { SingleSharesApproval } from "./singleSharesApproval";

export const SharesApprovalTable = ({
  singleCooperative,
  refresh,
  pageScrollProgress,
}: {
  singleCooperative: SingleCooperative;
  refresh: () => void;
  pageScrollProgress: number;
}) => {
  const [tab, setTab] = useState<"single" | "bulk">("single");
  const [singleQuery, setSingleQuery] = useState<SharesApprovalQuery>({
    limit: 10,
    offset: 0,
    coop: singleCooperative.id,
    filelog: undefined,
  });
  const [selectedEntry, setSelectedEntry] = useState<SingleSharesLog>();
  const [selectedBulkEntry, setSelectedBulkEntry] =
    useState<SingleBulkShareLog>();
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [actionDetails, setActionDetails] = useState<ApprovalActionDetails>();

  const modalControls = useModalControl();

  const { modal, modalOpen, handleModalOpen, handleModalClose } = modalControls;

  const {
    data: singleApprovals,
    isFetching: fetchingSingleApprovals,
    refetch: refetchSingleApprovals,
  } = useFetchPendingSharesApprovalsQuery(
    { ...singleQuery },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const {
    data: bulkApprovals,
    isFetching: fetchingBulkApprovals,
    refetch: refetchBulkApprovals,
  } = useFetchPendingSharesApprovalsQuery(
    {
      limit: 10,
      offset: 0,
      coop: singleCooperative.id,
      filelog: true,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const [doApproval, { isLoading: approving, isSuccess: approvalSuccess }] =
    useApproveSharesMutation();

  useEffect(() => {
    if (approvalSuccess) {
      if (tab === "single") {
        refetchSingleApprovals();
      } else {
        refetchBulkApprovals();
      }
      refresh();
      setActionDetails(undefined);
      setSelectedIds([]);
      setSelectedEntry(undefined);
    }
    // eslint-disable-next-line
  }, [approvalSuccess]);

  return (
    <>
      <div className="flex flex-col md:gap-8">
        <div
          className={classNames(
            "max-md:app-body-px md:py-0 z-10 transition-all duration-300 py-4",
            "flex items-center gap-4 justify-between flex-row",
            {
              "max-md:gap-0": selectedIds.length > 0,
              "max-md:sticky max-md:top-0 max-md:bg-light-green-bg/50 backdrop-blur-lg max-md:border-border-grey-3":
                pageScrollProgress >= 0 && selectedIds.length > 0,
              "bg-transparent border-none": pageScrollProgress < 0,
            }
          )}
        >
          <TabNav
            navs={[
              {
                title: "Single Uploads",
                to: "single",
                onClick: () => {
                  setTab("single");
                  setSelectedIds([]);
                },
                count:
                  (singleApprovals as SharesApprovalsResponse)?.data?.count ??
                  0,
              },
              {
                title: "Bulk Uploads",
                to: "bulk",
                onClick: () => {
                  setTab("bulk");
                  setSelectedIds([]);
                },
                count:
                  (bulkApprovals as SharesBulkApprovalsResponse)?.data
                    ?.length ?? 0,
              },
            ]}
            className={classNames(
              "md:w-fit overflow-scroll",
              {
                "w-0": selectedIds.length > 0,
                "w-full": selectedIds.length === 0,
              },
              "transition-all duration-300"
            )}
            activeTab={tab}
            variant="nav"
          />

          <ApprovalButtonSet
            onReject={async () => {
              setActionDetails({
                type: "multi",
                isApprove: false,
              });

              handleModalOpen("confirmationModal");
            }}
            onAccept={async () => {
              setActionDetails({
                type: "multi",
                isApprove: true,
              });

              handleModalOpen("confirmationModal");
            }}
            onClear={() => {
              setSelectedIds([]);
            }}
            acceptText={`Approve (${selectedIds.length})`}
            rejectText={`Decline (${selectedIds.length})`}
            className={classNames(
              "ml-auto overflow-hidden",
              {
                "max-md:w-full w-fit": selectedIds.length > 0,
                "!w-0": selectedIds.length === 0,
              },
              "transition-all duration-300"
            )}
            disableActions={approving}
          />
        </div>

        <div className="max-md:app-body-px">
          {tab === "single" ? (
            <SingleSharesApproval
              modalControls={modalControls}
              setSelectedIds={setSelectedIds}
              selectedIds={selectedIds}
              approvals={singleApprovals as SharesApprovalsResponse}
              setActionDetails={setActionDetails}
              setSelectedEntry={setSelectedEntry}
              fetching={fetchingSingleApprovals}
              query={singleQuery}
              setQuery={setSingleQuery}
            />
          ) : undefined}

          {tab === "bulk" ? (
            <BulkSharesApproval
              modalControls={modalControls}
              approvals={bulkApprovals as SharesBulkApprovalsResponse}
              setActionDetails={setActionDetails}
              setSelectedBulkEntry={setSelectedBulkEntry}
              fetching={fetchingBulkApprovals}
            />
          ) : undefined}
        </div>
      </div>

      {!!actionDetails ? (
        <ConfirmationModal
          isModalOpen={modalOpen && modal === "confirmationModal"}
          onModalClose={() => {
            handleModalClose();
          }}
          title={`Confirm ${actionDetails?.isApprove ? "Approval" : "Decline"}`}
          subtitle={
            <>
              {actionDetails.type === "single" && tab === "single" ? (
                <span>
                  You're about to{" "}
                  {actionDetails.isApprove ? "approve" : "decline"}{" "}
                  <span className="text-pry-green-200 font-medium">
                    {
                      selectedEntry?.MembershipSharesWalletID.MemberID.User
                        .FirstName
                    }{" "}
                    {
                      selectedEntry?.MembershipSharesWalletID.MemberID.User
                        .LastName
                    }
                    's
                  </span>{" "}
                  share upload of{" "}
                  <span className="text-pry-green-200 font-medium">
                    {formatToCurrency(selectedEntry?.Amount ?? "", {
                      decimalPlaces: 2,
                      currency: "NGN",
                    })}
                  </span>
                  .
                </span>
              ) : undefined}
              {actionDetails.type === "single" && tab === "bulk" ? (
                <span>
                  You're about to{" "}
                  {actionDetails.isApprove ? "approve" : "decline"}{" "}
                  <span className="text-pry-green-200 font-medium block line-clamp-1 text-ellipsis">
                    "{selectedBulkEntry?.filename}"
                  </span>
                </span>
              ) : undefined}
              {actionDetails.type === "multi" ? (
                <span>
                  You're about to{" "}
                  {actionDetails.isApprove ? "approve" : "decline"} a total of{" "}
                  <span className="text-pry-green-200 font-medium">
                    {selectedIds.length} selected
                  </span>{" "}
                  share uploads.
                </span>
              ) : undefined}
              <br />
              <br />
              ⚠️ This action is <strong>permanent</strong> and cannot be undone.
            </>
          }
          loading={approving}
          proceedButtonText={
            actionDetails?.isApprove ? "Yes, Approve" : "Yes, Decline"
          }
          onProceed={async () => {
            if (actionDetails?.type === "multi") {
              await doApproval({
                coop: singleCooperative.id,
                logs: selectedIds.map((id) => {
                  return {
                    id,
                    answer: actionDetails.isApprove,
                    isFile: tab === "bulk",
                  };
                }),
              });
            } else if (actionDetails?.type === "single") {
              await doApproval({
                coop: singleCooperative.id,
                logs: [
                  {
                    id: actionDetails.singleId!,
                    answer: actionDetails.isApprove,
                    isFile: tab === "bulk",
                  },
                ],
              });
            }
          }}
        />
      ) : undefined}

      {!!selectedEntry?.payslip ? (
        <ImageViewModal
          modalOpen={modalOpen && modal === "imageViewModal"}
          onModalClose={() => {
            handleModalClose();
            setSelectedEntry(undefined);
          }}
          src={selectedEntry?.payslip}
        />
      ) : undefined}

      {selectedBulkEntry ? (
        <BulkContributionsEntriesModal
          isModalOpen={modalOpen && modal === "viewSingleBultEntriesModal"}
          onModalClose={() => {
            handleModalClose();
            setSelectedBulkEntry(undefined);
          }}
          cooprativeDetails={singleCooperative}
          bulkEntry={selectedBulkEntry}
        />
      ) : undefined}
    </>
  );
};
